import { Inline, vars } from '@etg/wings';
import { css, etiColors } from '@eti/styles';
import { Check } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

export const ListItemStatus = {
  DONE: 'done',
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;
type ListItemStatusType = (typeof ListItemStatus)[keyof typeof ListItemStatus];

type ListItemType = {
  status?: ListItemStatusType;
  stepAriaLabel?: string;
  title?: string;
  description: ReactNode;
  descriptionAriaLabel?: string;
  shouldAnnounceAriaGrouped?: boolean;
};

const orderListProgressStyles = css`
  padding-left: 35px;
  position: relative;
`;

const orderListWrapperStyles = css`
  margin-bottom: 24px;
`;

const orderListCountStyles = (status?: ListItemStatusType) => css`
  align-items: center;
  background-color: ${status === ListItemStatus.DISABLED
    ? '#d9d9d9'
    : vars.colors.decoration.primary};
  ${status === ListItemStatus.ACTIVE && `border: 2px solid ${vars.colors.decoration.secondary};`}
  border-radius: 50%;
  color: ${status === ListItemStatus.DISABLED
    ? `${vars.colors.text.primary}`
    : `${etiColors.white}`};
  font-size: 0.625rem;
  font-weight: 700;
  height: 20px;
  left: 6px;
  position: absolute;
  width: 20px;
  z-index: 1;
`;

const orderListDescriptionStyles = (isLastItem: boolean, isItemDone: boolean) => css`
  font-size: 0.875rem;
  position: relative;

  &::before {
    border-left: 2px solid
      ${isItemDone ? vars.colors.decoration.primary : vars.colors.decoration.secondary};
    content: '';
    ${!isLastItem && 'height: calc(100% + 24px);'}
    display: inline-block;
    left: -20px;
    position: absolute;
    width: 10px;
  }
`;

export interface StyledOrderListProps {
  'data-testid'?: string;
  listItems: ListItemType[];
}

const StyledOrderList = ({ 'data-testid': dataTestId, listItems }: StyledOrderListProps) => {
  return (
    <div className={orderListProgressStyles} data-testid={dataTestId}>
      {listItems.map((listItem, index) => {
        const {
          stepAriaLabel,
          title,
          description,
          descriptionAriaLabel,
          shouldAnnounceAriaGrouped,
          status,
        } = listItem;
        const groupedStepAriaLabel = `${stepAriaLabel || index + 1}, ${
          title ? title.concat(', ') : ''
        }${descriptionAriaLabel || description}`;

        return (
          <div
            aria-label={shouldAnnounceAriaGrouped ? undefined : groupedStepAriaLabel}
            className={orderListWrapperStyles}
            data-testid={`styled-order-list-item-${index}`}
            key={`StyledOrderList${index}`}
          >
            <Inline align="center" aria-hidden className={orderListCountStyles(status)}>
              {status === ListItemStatus.DONE ? (
                <Check data-testid={`list-item-count-check-${index + 1}`} size="12" weight="bold" />
              ) : (
                index + 1
              )}
            </Inline>
            <div
              aria-hidden={Boolean(!shouldAnnounceAriaGrouped)}
              aria-label={shouldAnnounceAriaGrouped ? groupedStepAriaLabel : undefined}
              className={orderListDescriptionStyles(
                index === listItems.length - 1,
                status === ListItemStatus.DONE,
              )}
            >
              {title && (
                <p aria-hidden={Boolean(shouldAnnounceAriaGrouped)}>
                  <strong>{title}</strong>
                </p>
              )}
              {description}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StyledOrderList;
